import { useState } from 'react';

import { NOTES_TEXTAREA_SX } from '@/constants/app/forms';
import {
  adaptLegacyRedemption,
  deriveRedemptionState,
  formatRedemptionAmount,
  getActiveSettlement,
  getHistorySettlements,
  getRejectedBills,
  isRedemptionLocked,
  sumAssetUnits,
} from '@/helpers/redemption';
import { hasPermission } from '@/helpers/user';
import { useAppSelector } from '@/store';
import {
  useCancelRedemptionSettlementMutation,
  useCreateRedemptionSettlementMutation,
  useGetRedemptionMethodsQuery,
  useGetTransactionRedemptionQuery,
} from '@/store/api/services/redemptions';
import type { CancelRedemptionReason, RedemptionSettlement, TransactionDetail } from '@/types';
import { authReducer } from '@bitstopco/bitstop-theme';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Collapse, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  CANCEL_REASON_LABELS,
  SettlementCard,
  getMethodLabel,
} from './redemptionMethods/SettlementCard';
import { LEGACY_METHODS_FALLBACK, REDEMPTION_METHOD_REGISTRY } from './redemptionMethods/registry';
import type { RedemptionExtraFieldsValue } from './redemptionMethods/registry';
import {
  Banner,
  DetailRow,
  ERROR_LIGHT_BG,
  SUCCESS_LIGHT_BG,
  SectionLabel,
  StatusChip,
  WARNING_LIGHT_BG,
} from './statusUi';

// Unity can add reasons ahead of the client, so anything unlisted falls back to a neutral label
// instead of claiming a settlement is open.
const UNAVAILABLE_REASON_LABELS: Record<string, string> = {
  obligation_locked: "Can't create new settlement with open settlement",
  reversal_window_closed: 'Reversal window closed',
};

type TransactionRedemptionTabProps = {
  transaction: TransactionDetail;
  /** Stack the obligation and issue panels in one column (used by the narrow transaction drawer). */
  compact?: boolean;
  /** Closes the transaction drawer before the View Original Sale link navigates away. */
  hide?: () => void;
};

/**
 * On a claim sale (one that redeemed a settlement), athena returns the obligation of the sale
 * that originally failed -- this links back to that sale's Redemption tab.
 */
const OriginSaleLink = ({
  originSaleUuid,
  hide,
}: { originSaleUuid: string; hide?: () => void }) => {
  const navigate = useNavigate();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ p: 1.5, mb: 2, borderRadius: '12px', backgroundColor: 'neutral.100' }}
      data-test="redemption-origin-sale"
    >
      <Typography variant="body2" color="text.secondary">
        This sale redeemed a settlement for an earlier failed sale.
      </Typography>
      <Button
        onClick={() => {
          hide?.();
          navigate(`/sales/transactions/${originSaleUuid}/redemption`);
        }}
        size="small"
        endIcon={<OpenInNewIcon fontSize="small" />}
        sx={{ flexShrink: 0 }}
        data-test="redemption-origin-sale-link"
      >
        View Original Sale
      </Button>
    </Stack>
  );
};

const CANCEL_REASONS = (
  Object.entries(CANCEL_REASON_LABELS) as [CancelRedemptionReason, string][]
).map(([value, label]) => ({ value, label }));

// Display names for obligation asset types; anything unmapped falls back to the raw value.
const ASSET_TYPE_LABELS: Record<string, string> = {
  precious_metal: 'Goldback',
};

const getAssetTypeLabel = (assetType?: string | null) =>
  assetType ? (ASSET_TYPE_LABELS[assetType] ?? assetType) : '-';

const TransactionRedemptionTab = ({
  transaction,
  compact = false,
  hide,
}: TransactionRedemptionTabProps) => {
  const { data: redemptionResponse, isFetching: isFetchingRedemption } =
    useGetTransactionRedemptionQuery(transaction.id);
  const redemption = redemptionResponse?.data?.obligation
    ? redemptionResponse.data
    : adaptLegacyRedemption(transaction.redemption, transaction.rate);

  const dispenses = transaction.dispenses ?? [];
  const rejectedBills = getRejectedBills(dispenses);
  const state = deriveRedemptionState(dispenses, redemption);
  const obligation = redemption?.obligation ?? null;
  const locked = isRedemptionLocked(state);

  const purchasedUnits = sumAssetUnits(dispenses.flatMap((d) => d.expected_bills));
  const owedUnits = obligation?.asset_amount ?? sumAssetUnits(rejectedBills);
  const outstandingUnits = obligation?.outstanding_asset_amount ?? owedUnits;

  const active = getActiveSettlement(redemption);
  const history = getHistorySettlements(redemption);

  const isTerminal = state === 'noObligation' || state === 'noFailure' || state === 'fulfilled';
  // A fulfilled obligation (or one with nothing left owed) can never take another settlement.
  const nothingOutstanding = state === 'fulfilled' || outstandingUnits <= 0;
  // Every Unity redemption route (methods, issue, notes, cancel) requires redemptions.modify;
  // reading the redemption itself only needs sales.view, so without it the tab is read-only.
  // Read straight from the user, not usePermissions: the transaction drawer is a modal mounted
  // above Main's PermissionsProvider, where usePermissions always answers false.
  const userData = useAppSelector(authReducer.selectors.userInfo);
  const canModify = hasPermission(userData?.permissions, 'redemptions.modify');
  const {
    data: methodsResponse,
    isError: methodsError,
    isFetching: isFetchingMethods,
  } = useGetRedemptionMethodsQuery(obligation?.id ?? '', {
    skip: isTerminal || !obligation || !canModify,
  });
  // An empty list is a real answer from Unity (no active methods for this payment method), so
  // the legacy fallback only stands in when the request itself fails.
  const methods = methodsError ? LEGACY_METHODS_FALLBACK : (methodsResponse?.data ?? []);
  const noMethodsConfigured = !methodsError && methodsResponse != null && methods.length === 0;

  const [selectedMethod, setSelectedMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [extraFields, setExtraFields] = useState<RedemptionExtraFieldsValue>({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<CancelRedemptionReason | ''>('');
  const [cancelNotes, setCancelNotes] = useState('');
  // With "other" the notes are the cancellation reason itself, so they're required.
  const cancelRequiresNotes = cancelReason === 'other';
  const [historyOpen, setHistoryOpen] = useState(false);

  const selectedDefinition = REDEMPTION_METHOD_REGISTRY[selectedMethod];
  const extraFieldsValid =
    !selectedDefinition?.isExtraFieldsValid || selectedDefinition.isExtraFieldsValid(extraFields);

  const isDebitReversal = selectedMethod === 'debit_reversal';
  const outstandingFiat =
    obligation?.fiat_amount != null ? `$${Number(obligation.fiat_amount).toFixed(2)}` : null;
  const debitReversalAvailable = methods.some((m) => m.name === 'debit_reversal' && m.applicable);
  const outstandingAmountDisplay =
    isDebitReversal || debitReversalAvailable
      ? formatRedemptionAmount('debit_reversal', outstandingUnits, obligation?.fiat_amount)
      : `${outstandingUnits} GB`;

  const handleSelectMethod = (name: string) => {
    setSelectedMethod((current) => (current === name ? '' : name));
    setExtraFields({});
  };

  const [createRedemptionSettlement, { isLoading: isIssuing }] =
    useCreateRedemptionSettlementMutation();
  const [cancelRedemptionSettlement, { isLoading: isCancelling }] =
    useCancelRedemptionSettlementMutation();
  // A cancel (or issue) changes which methods Unity allows, so the panel holds still from the
  // click until both the redemption and its methods have refetched, instead of showing the stale
  // locks.
  const panelBusy = isCancelling || isFetchingRedemption || isFetchingMethods;

  const handleIssueRedemption = async () => {
    if (!obligation) return;
    try {
      const { notesSaved } = await createRedemptionSettlement({
        obligationId: obligation.id,
        txId: transaction.id,
        body: {
          method: selectedMethod,
          asset_amount: obligation.outstanding_asset_amount,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      }).unwrap();

      toast.success('Redemption issued');
      if (!notesSaved) {
        toast.error('Redemption was issued, but the notes could not be saved');
      }
      setSelectedMethod('');
      setNotes('');
      setExtraFields({});
    } catch {
      toast.error('Failed to issue redemption');
    }
  };

  const handleCancelSettlement = async () => {
    if (!active || !cancelReason) return;
    try {
      await cancelRedemptionSettlement({
        settlementId: active.id,
        txId: transaction.id,
        body: {
          reason: cancelReason,
          ...(cancelNotes.trim() ? { notes: cancelNotes.trim() } : {}),
        },
      }).unwrap();

      toast.success('Redemption cancelled');
      setCancelOpen(false);
      setCancelReason('');
      setCancelNotes('');
    } catch (error) {
      // Unity explains a refused cancel (409): already claimed at a machine, awaiting
      // reconciliation, or already resolved.
      const reason = (error as { data?: { detail?: { message?: string } } })?.data?.detail?.message;
      toast.error(reason ? `Couldn't cancel: ${reason}` : 'Failed to cancel redemption');
    }
  };

  if (state === 'noObligation') {
    return (
      <Box pt={2} data-test="transaction-redemption-tab">
        {redemption.origin_sale_uuid && (
          <OriginSaleLink originSaleUuid={redemption.origin_sale_uuid} hide={hide} />
        )}
        <Box data-test="redemption-state-noObligation">
          <Banner
            icon={<WarningAmberIcon fontSize="inherit" />}
            color="#B45309"
            backgroundColor={WARNING_LIGHT_BG}
            title="Shortfall Detected"
            description={`Customer is short ${owedUnits} Goldback${owedUnits === 1 ? '' : 's'}. Unity creates the redemption obligation automatically. This view will update once it syncs, and there is no dashboard action to create one manually.`}
          />
          <SectionLabel>Shortfall</SectionLabel>
          <Box sx={{ backgroundColor: 'neutral.100', borderRadius: '12px', px: 2 }}>
            {rejectedBills.map((bill, index) => (
              <Box
                key={`${bill.bill_note_id}-${index}`}
                data-test={`redemption-shortfall-row-${bill.bill_note_id}`}
              >
                <DetailRow
                  label={bill.display_name}
                  value={`${bill.quantity} × ${bill.unit} GB`}
                  last={index === rejectedBills.length - 1}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  if (state === 'noFailure') {
    return (
      <Box pt={2} data-test="transaction-redemption-tab">
        {redemption.origin_sale_uuid && (
          <OriginSaleLink originSaleUuid={redemption.origin_sale_uuid} hide={hide} />
        )}
        <Box data-test="redemption-state-noFailure">
          <Banner
            icon={<CheckCircleIcon fontSize="inherit" />}
            color="#2E7D32"
            backgroundColor={SUCCESS_LIGHT_BG}
            title="No Dispense Failure"
            description={`All ${purchasedUnits} Goldback${purchasedUnits === 1 ? '' : 's'} were dispensed successfully.`}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box pt={2} data-test="transaction-redemption-tab">
      {redemption.origin_sale_uuid && (
        <OriginSaleLink originSaleUuid={redemption.origin_sale_uuid} hide={hide} />
      )}
      <Box data-test={`redemption-state-${state}`}>
        {state === 'short' && (
          <Banner
            icon={<WarningAmberIcon fontSize="inherit" />}
            color="#C62828"
            backgroundColor={ERROR_LIGHT_BG}
            title="Delivery Came Up Short"
            description="The last attempt delivered less than expected. The obligation continues for the remainder below."
          />
        )}
        {state === 'unknownLock' && (
          <Banner
            icon={<WarningAmberIcon fontSize="inherit" />}
            color="#B45309"
            backgroundColor={WARNING_LIGHT_BG}
            title="Awaiting Response"
            description="The switch hasn't responded to the last debit reversal. It may still go through or may have failed. No action is available until it resolves."
          />
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={compact || !canModify ? 12 : 6}>
            <Stack spacing={2}>
              <Box
                data-test="redemption-obligation-card"
                sx={{ backgroundColor: 'neutral.100', borderRadius: '12px', px: 2, pt: 1.5 }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <SectionLabel>Obligation</SectionLabel>
                  {obligation && (
                    <StatusChip
                      label={obligation.status}
                      sx={{ textTransform: 'capitalize' }}
                      color={
                        obligation.status === 'completed'
                          ? 'success'
                          : obligation.status === 'cancelled'
                            ? 'error'
                            : 'warning'
                      }
                      data-test="redemption-obligation-status-chip"
                    />
                  )}
                </Stack>
                <DetailRow label="Total Owed" value={`${owedUnits} GB`} />
                <DetailRow label="Total Delivered" value={`${owedUnits - outstandingUnits} GB`} />
                <DetailRow label="Total Outstanding" value={`${outstandingUnits} GB`} emphasize />
                {compact ? (
                  <DetailRow label="Asset" value={getAssetTypeLabel(obligation?.asset_type)} last />
                ) : (
                  <DetailRow
                    label="Asset Exchange Rate"
                    value={
                      obligation
                        ? `$${Number(obligation.asset_rate).toFixed(2)} ${obligation.fiat_type}/GB`
                        : '-'
                    }
                    last
                  />
                )}
              </Box>

              {active && (
                <Box data-test="redemption-active-settlement">
                  <SectionLabel>Active Settlement</SectionLabel>
                  <SettlementCard settlement={active} />
                  {canModify && active.status === 'issued' && !cancelOpen && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ mt: 1 }}
                      onClick={() => setCancelOpen(true)}
                      data-test="redemption-cancel-open"
                    >
                      Cancel Redemption
                    </Button>
                  )}
                  {cancelOpen && (
                    <Box
                      sx={{ mt: 1.5, p: 2, backgroundColor: 'neutral.100', borderRadius: '12px' }}
                    >
                      <TextField
                        select
                        label="Reason"
                        fullWidth
                        size="small"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value as CancelRedemptionReason)}
                        inputProps={{ 'data-test': 'redemption-cancel-reason' }}
                        sx={{ mb: 1.5 }}
                      >
                        {CANCEL_REASONS.map((r) => (
                          <MenuItem key={r.value} value={r.value}>
                            {r.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {cancelRequiresNotes ? 'Reason' : 'Notes (optional)'}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder={
                          cancelRequiresNotes
                            ? 'Describe why this redemption is being cancelled...'
                            : 'Add any context for this cancellation...'
                        }
                        value={cancelNotes}
                        onChange={(e) => setCancelNotes(e.target.value)}
                        inputProps={{ 'data-test': 'redemption-cancel-notes' }}
                        sx={{ mb: 1.5, ...NOTES_TEXTAREA_SX }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          onClick={() => setCancelOpen(false)}
                          disabled={isCancelling}
                        >
                          Back
                        </Button>
                        <LoadingButton
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={!cancelReason || (cancelRequiresNotes && !cancelNotes.trim())}
                          loading={isCancelling}
                          onClick={handleCancelSettlement}
                          data-test="redemption-cancel-confirm"
                        >
                          Confirm Cancel
                        </LoadingButton>
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}

              {!compact ? (
                history.length > 0 && (
                  <Box data-test="redemption-history">
                    <SectionLabel>Previous Settlements</SectionLabel>
                    <Stack spacing={1.5}>
                      {history.map((settlement: RedemptionSettlement, index) => (
                        <SettlementCard
                          key={settlement.id}
                          settlement={settlement}
                          collapsible
                          defaultOpen={!active && index === 0}
                        />
                      ))}
                    </Stack>
                  </Box>
                )
              ) : (redemption?.settlements ?? []).length === 0 ? (
                <Box data-test="redemption-settlements-empty">
                  <SectionLabel>Settlements</SectionLabel>
                  <Typography variant="body2" color="text.secondary">
                    Unsettled Redemption
                  </Typography>
                </Box>
              ) : (
                history.length > 0 && (
                  <Box data-test="redemption-history">
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      onClick={() => setHistoryOpen((open) => !open)}
                      sx={{ cursor: 'pointer' }}
                      data-test="redemption-history-toggle"
                    >
                      <SectionLabel>Previous Settlements ({history.length})</SectionLabel>
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          mb: 1,
                          transition: 'transform 150ms',
                          transform: historyOpen ? 'rotate(180deg)' : 'none',
                        }}
                      />
                    </Stack>
                    <Collapse in={historyOpen}>
                      <Stack spacing={1.5}>
                        {history.map((settlement: RedemptionSettlement, index) => (
                          <SettlementCard
                            key={settlement.id}
                            settlement={settlement}
                            collapsible
                            defaultOpen={!active && index === 0}
                          />
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                )
              )}
            </Stack>
          </Grid>

          {canModify && (
            <Grid item xs={12} md={compact ? 12 : 6}>
              <Stack spacing={2}>
                <Box
                  data-test="redemption-issue-panel"
                  sx={{ backgroundColor: 'neutral.100', borderRadius: '12px', p: 2 }}
                >
                  <SectionLabel>Issue Redemption</SectionLabel>

                  {nothingOutstanding ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      data-test="redemption-nothing-outstanding"
                    >
                      Nothing is outstanding on this obligation, so no further redemptions can be
                      issued.
                    </Typography>
                  ) : (
                    <>
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        {noMethodsConfigured && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            data-test="redemption-methods-empty"
                          >
                            No redemption methods configured for this payment method.
                          </Typography>
                        )}
                        {methods.map((method) => {
                          const definition = REDEMPTION_METHOD_REGISTRY[method.name];
                          if (!definition) {
                            return (
                              <Box
                                key={method.name}
                                data-test="redemption-method-unknown-error"
                                sx={{
                                  p: 1.5,
                                  borderRadius: '8px',
                                  backgroundColor: ERROR_LIGHT_BG,
                                }}
                              >
                                <Typography variant="body2" color="error.main">
                                  Unknown redemption method "{method.name}". Contact engineering.
                                </Typography>
                              </Box>
                            );
                          }

                          const selected = selectedMethod === method.name;
                          const disabled = !method.applicable || panelBusy;
                          const refusalsLabel =
                            method.max_refusals != null
                              ? `${method.refusals}/${method.max_refusals} refusals`
                              : `${method.refusals} refusal${method.refusals === 1 ? '' : 's'}`;

                          return (
                            <Box key={method.name}>
                              <Box
                                data-test={`redemption-method-${method.name}`}
                                onClick={() => !disabled && handleSelectMethod(method.name)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  p: 1.5,
                                  borderRadius: '10px',
                                  border: '2px solid',
                                  borderColor: selected ? 'error.main' : 'divider',
                                  backgroundColor: selected ? ERROR_LIGHT_BG : 'transparent',
                                  cursor: disabled ? 'default' : 'pointer',
                                  opacity: disabled ? 0.5 : 1,
                                }}
                              >
                                <Box flex={1} minWidth="max-content">
                                  <Typography variant="body2" fontWeight="bold" noWrap>
                                    {getMethodLabel(method.name)}
                                  </Typography>
                                </Box>
                                {/* Earlier refusals warn while the method is still usable, ahead of the cap. */}
                                {!disabled && method.refusals > 0 && (
                                  <StatusChip
                                    color="warning"
                                    variant="outlined"
                                    label={refusalsLabel}
                                    data-test={`redemption-method-${method.name}-refusals`}
                                  />
                                )}
                                {!method.applicable ? (
                                  <StatusChip
                                    label={
                                      method.reason === 'max_refusals_reached'
                                        ? refusalsLabel
                                        : (UNAVAILABLE_REASON_LABELS[method.reason ?? ''] ??
                                          'Unavailable')
                                    }
                                    data-test={`redemption-method-${method.name}-reason`}
                                    // Long reasons wrap inside the chip so the method name keeps its width.
                                    sx={{
                                      height: 'auto',
                                      maxWidth: '60%',
                                      py: 0.5,
                                      '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                      },
                                    }}
                                  />
                                ) : selected ? (
                                  <CheckCircleIcon color="error" fontSize="small" />
                                ) : (
                                  <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                )}
                              </Box>
                              {selected && definition.ExtraFields && (
                                <Box sx={{ px: 1.5, py: 1 }}>
                                  {definition.extraFieldsModal && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      {definition.extraFieldsModal.subtitle}
                                    </Typography>
                                  )}
                                  <definition.ExtraFields
                                    value={extraFields}
                                    onChange={setExtraFields}
                                  />
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>

                      {locked && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 2 }}
                          data-test="redemption-locked-notice"
                        >
                          {state === 'unknownLock'
                            ? 'Locked while the last attempt is in doubt with the switch.'
                            : `An issued ${getMethodLabel(active?.method ?? '')} is outstanding. Cancel it to choose a different method.`}
                        </Typography>
                      )}

                      <TextField
                        label="Amount"
                        helperText="Fixed to the outstanding balance"
                        fullWidth
                        size="small"
                        value={outstandingAmountDisplay}
                        disabled
                        sx={{ mb: 2 }}
                      />

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Notes
                      </Typography>
                      <TextField
                        placeholder="Add any context for this redemption..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={locked || panelBusy}
                        inputProps={{ 'data-test': 'redemption-notes' }}
                        sx={{ mb: 2, ...NOTES_TEXTAREA_SX }}
                      />

                      <LoadingButton
                        data-test="redemption-issue"
                        variant="contained"
                        color="error"
                        fullWidth
                        disabled={locked || panelBusy || !selectedMethod || !extraFieldsValid}
                        loading={isIssuing}
                        onClick={handleIssueRedemption}
                      >
                        Issue Redemption (
                        {isDebitReversal && outstandingFiat
                          ? outstandingFiat
                          : `${outstandingUnits} GB`}
                        )
                      </LoadingButton>
                    </>
                  )}
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default TransactionRedemptionTab;
